/**
 * CostEtlImportAdNetworkSpendComputeNetMargiPage — net-margin report for imported ad-network spend.
 * GET /api/v1/cost-etl-import-ad-network-spend-compute-net-margi/margin returns aggregated rows:
 * (campaign_id, date, spend_cents, revenue_cents, margin_cents, clicks, impressions, conversions).
 */
import { useState } from "react";
import {
	useCostEtlImportAdNetworkSpendComputeNetMargiMargin,
	useCostEtlImportAdNetworkSpendComputeNetMargiSpendList,
} from "./hooks";
import type { MarginRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-green-500 focus:outline-none";

function fmtCents(cents: number): string {
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString()}.${rem.toString().padStart(2, "0")}`;
}

export function CostEtlImportAdNetworkSpendComputeNetMargiPage() {
	const [campaign, setCampaign] = useState("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");

	const filters = {
 campaign_id: campaign || undefined,
 from: from || undefined,
 to: to || undefined,
	};

	const marginQuery = useCostEtlImportAdNetworkSpendComputeNetMargiMargin(filters);
	const spendQuery = useCostEtlImportAdNetworkSpendComputeNetMargiSpendList();

	const items: MarginRow[] = marginQuery.data?.items ?? [];

	const totals = items.reduce(
 (acc, r) => ({
 spend: acc.spend + (r.spend_cents || 0),
 revenue: acc.revenue + (r.revenue_cents || 0),
 margin: acc.margin + (r.margin_cents || 0),
 clicks: acc.clicks + (r.clicks || 0),
 impressions: acc.impressions + (r.impressions || 0),
 conversions: acc.conversions + (r.conversions || 0),
 }),
 { spend: 0, revenue: 0, margin: 0, clicks: 0, impressions: 0, conversions: 0 },
	);

	return (
 <main
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-page"
 className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Cost ETL</p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Net Margin Report
 </h1>
 <p className="text-sm text-zinc-600">
 Imported ad-network spend vs conversions revenue. Margin = revenue − spend.
 </p>
 </header>

 <section className={CARD} aria-label="Filters">
 <div
 className="flex flex-wrap items-end gap-4"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-filters"
 >
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-600">Campaign ID</span>
 <input
 type="text"
 className={INPUT}
 placeholder="any"
 value={campaign}
 onChange={(e) => setCampaign(e.target.value)}
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-campaign"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-600">From</span>
 <input
 type="date"
 className={INPUT}
 value={from}
 onChange={(e) => setFrom(e.target.value)}
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-from"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-600">To</span>
 <input
 type="date"
 className={INPUT}
 value={to}
 onChange={(e) => setTo(e.target.value)}
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-to"
 />
 </label>
 </div>
 </section>

 <section
 className="grid grid-cols-1 gap-4 sm:grid-cols-3"
 aria-label="Totals"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-totals"
 >
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Spend</p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {fmtCents(totals.spend)}
 </p>
 </div>
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Revenue</p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {fmtCents(totals.revenue)}
 </p>
 </div>
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Margin</p>
 <p
 className={`mt-1 text-2xl font-semibold tabular-nums ${
 totals.margin >= 0 ? "text-green-700" : "text-red-600"
 }`}
 >
 {fmtCents(totals.margin)}
 </p>
 </div>
 </section>

 <section className={CARD} aria-label="Margin report">
 {marginQuery.isLoading ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-loading"
 >
 Loading.
 </p>
 ) : marginQuery.error ? (
 <p className="text-sm text-red-600" role="alert">
 {marginQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-empty"
 >
 No data for the selected filters.
 </p>
 ) : (
 <div className="overflow-x-auto">
 <table
 className="w-full border-collapse"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-table"
 >
 <thead className="bg-zinc-50">
 <tr>
 <th className={TH}>Date</th>
 <th className={TH}>Campaign</th>
 <th className={`${TH} text-right`}>Impressions</th>
 <th className={`${TH} text-right`}>Clicks</th>
 <th className={`${TH} text-right`}>Conversions</th>
 <th className={`${TH} text-right`}>Spend</th>
 <th className={`${TH} text-right`}>Revenue</th>
 <th className={`${TH} text-right`}>Margin</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-200">
 {items.map((r) => {
 const key = `${r.campaign_id}__${r.date}`;
 return (
 <tr
 key={key}
 data-testid={`cost-etl-import-ad-network-spend-compute-net-margi-row-${key}`}
 >
 <td className={TD}>{r.date}</td>
 <td className={TD}>{r.campaign_id}</td>
 <td className={`${TD} text-right tabular-nums`}>
 {(r.impressions || 0).toLocaleString()}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {(r.clicks || 0).toLocaleString()}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {(r.conversions || 0).toLocaleString()}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {fmtCents(r.spend_cents || 0)}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {fmtCents(r.revenue_cents || 0)}
 </td>
 <td
 className={`${TD} text-right tabular-nums ${
 (r.margin_cents || 0) >= 0 ? "text-green-700" : "text-red-600"
 }`}
 >
 {fmtCents(r.margin_cents || 0)}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </section>

 <section className={CARD} aria-label="Recent spend imports">
 <h2 className="text-green-700 font-semibold mb-3 text-sm font-semibold text-zinc-900">Recent Spend Imports</h2>
 {spendQuery.isLoading ? (
 <p className="text-sm text-zinc-500">Loading.</p>
 ) : spendQuery.error ? (
 <p className="text-sm text-red-600" role="alert">
 {spendQuery.error.message}
 </p>
 ) : (spendQuery.data?.items ?? []).length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-spend-empty"
 >
 No spend imports yet.
 </p>
 ) : (
 <div className="overflow-x-auto">
 <table
 className="w-full border-collapse"
 data-testid="cost-etl-import-ad-network-spend-compute-net-margi-spend-table"
 >
 <thead className="bg-zinc-50">
 <tr>
 <th className={TH}>Date</th>
 <th className={TH}>Network</th>
 <th className={TH}>Campaign</th>
 <th className={TH}>External ID</th>
 <th className={`${TH} text-right`}>Spend</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-200">
 {(spendQuery.data?.items ?? []).slice(0, 25).map((row) => (
 <tr
 key={row.id}
 data-testid={`cost-etl-import-ad-network-spend-compute-net-margi-spend-row-${row.id}`}
 >
 <td className={TD}>{row.date}</td>
 <td className={TD}>{row.network}</td>
 <td className={TD}>{row.campaign_id}</td>
 <td className={TD}>{row.external_id}</td>
 <td className={`${TD} text-right tabular-nums`}>
 {fmtCents(row.spend_cents || 0)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </main>
	);
}

export default CostEtlImportAdNetworkSpendComputeNetMargiPage;
